"use client";

import { useCallback, useEffect, useRef } from "react";

import type { HeaderSection } from "@/entities/global";
import { LogoBig } from "@/shared/icons/LogoBig";
import { ThemeToggle } from "@/shared/ui/theme-toggle/ThemeToggle";
import { StrapiImage } from "@/shared/ui/strapi-image";
import { Container } from "@/shared/ui/container/Container";

import { useThrottledScroll } from "../hooks/useThrottledScroll";

import { HeaderBurger } from "./HeaderBurger";
import { HeaderNav } from "./HeaderNav";

import styles from "./Header.module.css";

const SCROLL_THRESHOLD = 300;

type HeaderViewProps = {
  data: HeaderSection;
};

export function HeaderView({ data }: HeaderViewProps) {
  const { logo, navItems } = data;
  const scrollY = useThrottledScroll({ throttleMs: 16 });

  const headerRef = useRef<HTMLElement>(null);
  const slotRef = useRef<HTMLDivElement>(null);
  const wasTinyRef = useRef(false);

  const updateHeaderMode = useCallback(() => {
    const header = headerRef.current;
    const slot = slotRef.current;
    if (!header || !slot) return;

    const isScrolled = scrollY > SCROLL_THRESHOLD;

    if (isScrolled && !wasTinyRef.current) {
      slot.style.setProperty("--header-slot-height", `${header.offsetHeight}px`);
      slot.setAttribute("data-header-compensate", "true");
      header.setAttribute("data-header-tiny", "true");
      wasTinyRef.current = true;
    } else if (!isScrolled && wasTinyRef.current) {
      header.setAttribute("data-header-tiny", "false");
      slot.removeAttribute("data-header-compensate");
      slot.style.removeProperty("--header-slot-height");
      wasTinyRef.current = false;
    }
  }, [scrollY]);

  useEffect(() => {
    updateHeaderMode();
  }, [updateHeaderMode]);

  return (
    <div ref={slotRef} className={styles.headerSlot}>
      <header ref={headerRef} className={styles.header}>
        <Container className={styles.content}>
          {logo?.image?.url ? (
            <StrapiImage
              image={logo.image}
              width={213}
              height={36}
              className={styles.logo}
              alt={logo.label ?? "Эвотор"}
            />
          ) : (
            <LogoBig className={styles.logo} />
          )}
          <div className={styles.desktopActions}>
            <HeaderNav items={navItems} />
            <ThemeToggle />
          </div>

          <HeaderBurger>
            <HeaderNav items={navItems} className={styles.navMobile} />
            <ThemeToggle />
          </HeaderBurger>
        </Container>
      </header>
    </div>
  );
}
